/**
 * Formulário de produto da etapa 2: valores em texto (como a pessoa digita), as mesmas recusas que o
 * servidor faz (§3.2) e a conversão para o corpo da requisição. A categoria e a classe não saem
 * daqui: são do servidor.
 */
import type {
  FlammablePhysicalState,
  FlammableSubstance,
  FlammableSubstanceFormData,
} from '../../../../../types/flammableInstallation';

export interface ProdutoValues {
  productName: string;
  physicalState: FlammablePhysicalState;
  /** Só vírgula como separador decimal; só vale em líquido. */
  flashPoint: string;
  storedHeated: boolean;
  capacity: string;
  storageLocation: string;
  sdsAvailable: boolean;
  observations: string;
}

export type ProdutoErrors = Partial<Record<keyof ProdutoValues, string>>;

export const PRODUTO_VAZIO: ProdutoValues = {
  productName: '',
  physicalState: 'LIQUIDO',
  flashPoint: '',
  storedHeated: false,
  capacity: '',
  storageLocation: '',
  sdsAvailable: false,
  observations: '',
};

const decimalEmTexto = (valor: number): string => String(valor).replace('.', ',');

export const valoresDoProduto = (produto: FlammableSubstance): ProdutoValues => ({
  productName: produto.productName,
  physicalState: produto.physicalState,
  flashPoint: produto.flashPointCelsius === null ? '' : decimalEmTexto(produto.flashPointCelsius),
  storedHeated: produto.storedHeated,
  capacity: decimalEmTexto(produto.capacity),
  storageLocation: produto.storageLocation ?? '',
  sdsAvailable: produto.sdsAvailable,
  observations: produto.observations ?? '',
});

const comoNumero = (texto: string): number => Number(texto.trim().replace(',', '.'));

const FORMATO_DECIMAL = /^-?\d+(,\d+)?$/;

/** Em pt-BR o ponto separa milhar: aceitá-lo como decimal gravaria "10.000" como 10. */
const TEM_PONTO = /\./;
const AVISO_DO_PONTO = 'Use vírgula para as casas decimais, por exemplo 10000,5. Não use ponto de milhar.';

/** Zeros à direita não contam como casa decimal (o servidor também não conta). */
const casasDecimais = (texto: string): number => texto.trim().split(',')[1]?.replace(/0+$/, '').length ?? 0;

export const validarProduto = (values: ProdutoValues): ProdutoErrors => {
  const errors: ProdutoErrors = {};

  if (values.productName.trim() === '') errors.productName = 'Diga o nome do produto.';
  else if (values.productName.trim().length > 200) errors.productName = 'Use até 200 letras.';

  if (values.physicalState === 'LIQUIDO') {
    const texto = values.flashPoint.trim();
    if (texto === '') errors.flashPoint = 'Líquido precisa do ponto de fulgor, em °C. Ex.: 38';
    else if (TEM_PONTO.test(texto)) errors.flashPoint = AVISO_DO_PONTO;
    else if (!FORMATO_DECIMAL.test(texto)) errors.flashPoint = 'Use só números, com vírgula se precisar. Ex.: 38,5';
    else if (casasDecimais(texto) > 1) errors.flashPoint = 'Use no máximo uma casa depois da vírgula. Ex.: 38,5';
    else if (comoNumero(texto) < -100 || comoNumero(texto) > 1000) {
      errors.flashPoint = 'O ponto de fulgor fica entre −100 e 1.000 °C.';
    }
  }

  const capacidade = values.capacity.trim();
  if (capacidade === '') errors.capacity = 'Diga a capacidade armazenada.';
  else if (TEM_PONTO.test(capacidade)) errors.capacity = AVISO_DO_PONTO;
  else if (!FORMATO_DECIMAL.test(capacidade)) errors.capacity = 'Use só números, com vírgula se precisar. Ex.: 12,5';
  else if (casasDecimais(capacidade) > 3) errors.capacity = 'Use no máximo três casas depois da vírgula. Ex.: 12,5';
  else if (comoNumero(capacidade) <= 0) errors.capacity = 'A capacidade precisa ser maior que zero.';
  else if (comoNumero(capacidade) > 1_000_000_000) errors.capacity = 'A capacidade passa de 1 bilhão. Confira o número.';

  return errors;
};

const textoOuNulo = (texto: string): string | null => (texto.trim() === '' ? null : texto.trim());

/** Gás não leva ponto de fulgor nem "aquecido": o servidor recusa o primeiro e ignora o segundo. */
export const produtoParaEnvio = (values: ProdutoValues): FlammableSubstanceFormData => {
  const base: FlammableSubstanceFormData = {
    productName: values.productName.trim(),
    physicalState: values.physicalState,
    capacity: comoNumero(values.capacity),
    sdsAvailable: values.sdsAvailable,
    storageLocation: textoOuNulo(values.storageLocation),
    observations: textoOuNulo(values.observations),
    storedHeated: values.physicalState === 'LIQUIDO' && values.storedHeated,
  };
  if (values.physicalState === 'LIQUIDO') base.flashPointCelsius = comoNumero(values.flashPoint);
  return base;
};

export const UNIDADE_DO_ESTADO: Readonly<Record<FlammablePhysicalState, string>> = {
  GAS: 'toneladas',
  LIQUIDO: 'm³',
};

export const UNIDADE_CURTA_DO_ESTADO: Readonly<Record<FlammablePhysicalState, string>> = {
  GAS: 't',
  LIQUIDO: 'm³',
};
